import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

/**
 * GDPR / ePrivacy cookie consent banner (Hungarian) with Google Consent Mode v2.
 *
 * How it works:
 *  • On first visit `localStorage.getItem('cookie_consent')` is null →
 *    the banner renders as a bottom-of-page card.
 *  • "Elfogadom" → localStorage `cookie_consent = 'granted'` + calls
 *    `gtag('consent', 'update', { analytics_storage: 'granted', ... })`.
 *  • "Elutasítom" → localStorage `cookie_consent = 'denied'` — the gtag
 *    defaults (all denied) stay in effect, GA sends cookieless pings only.
 *  • Users can revoke consent anytime by visiting /adatvedelem and clicking
 *    the "Cookie beállítások módosítása" button, which fires
 *    `window.reopenCookieConsent()` (exposed globally by this component).
 *
 * Notes:
 *  • This banner blocks nothing — GA snippet always loads (Consent Mode v2
 *    requires this so Google can send anonymized modelling pings). It's the
 *    consent SIGNALS that switch on/off, not the script itself.
 *  • Component intentionally stays out of React Portal / focus-trap complexity
 *    — a simple `position: fixed` bar is more than sufficient for a legal
 *    consent gate, and every button has a `data-testid` for e2e testing.
 */
const CONSENT_KEY = 'cookie_consent';

const updateConsent = (granted) => {
  if (typeof window === 'undefined') return;
  const state = granted ? 'granted' : 'denied';

  // ---- Google Consent Mode v2 signal update ---------------------------------
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: state,
      ad_storage:        state,
      ad_user_data:      state,
      ad_personalization:state,
    });
  }

  // ---- Meta Pixel consent gate ---------------------------------------------
  // The base snippet in index.html called `fbq('consent','revoke')` right
  // after init; here we flip it to grant (and fire the initial PageView)
  // or leave it revoked. Guarded — if the pixel didn't load (no ID), the
  // helper is undefined and we silently skip.
  if (granted && typeof window.metaPixelGrantConsent === 'function') {
    window.metaPixelGrantConsent();
  } else if (!granted && typeof window.metaPixelRevokeConsent === 'function') {
    window.metaPixelRevokeConsent();
  }
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  const openBanner = useCallback(() => setVisible(true), []);

  useEffect(() => {
    // Show banner if no decision has been recorded yet.
    if (typeof window === 'undefined') return;
    const decision = window.localStorage.getItem(CONSENT_KEY);
    if (!decision) setVisible(true);
    // Sync any previously-persisted decision into gtag (in case the user
    // reloaded and gtag was reinitialized with 'denied' defaults).
    if (decision === 'granted') updateConsent(true);
    // Expose a global re-open hook so the Adatvedelem page can revoke.
    window.reopenCookieConsent = openBanner;
    return () => {
      if (window.reopenCookieConsent === openBanner) {
        delete window.reopenCookieConsent;
      }
    };
  }, [openBanner]);

  const handleAccept = () => {
    window.localStorage.setItem(CONSENT_KEY, 'granted');
    updateConsent(true);
    setVisible(false);
  };

  const handleReject = () => {
    window.localStorage.setItem(CONSENT_KEY, 'denied');
    updateConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:max-w-md z-[9999] bg-white border border-gray-200 shadow-2xl rounded-xl p-5 md:p-6"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <h2
        id="cookie-consent-title"
        className="text-base font-bold text-gray-900 mb-2"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        Sütik, analitika és marketing
      </h2>
      <p className="text-sm text-gray-700 leading-relaxed">
        Az oldal a látogatottság mérésére <strong>Google Analytics</strong>, a hirdetési kampányok
        eredményének követésére pedig <strong>Meta Pixel (Facebook)</strong> sütiket használ,
        anonimizált IP-címmel. Ezek segítenek jobb felhasználói élményt nyújtani és a hirdetéseket
        célzottabbá tenni. Az elengedhetetlen (session, biztonsági) sütiket enélkül is használjuk.
        Részletek az{' '}
        <Link
          to="/adatvedelem"
          className="text-yellow-700 hover:text-yellow-600 font-semibold underline"
          data-testid="cookie-consent-policy-link"
        >
          Adatkezelési Tájékoztatóban
        </Link>.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <button
          type="button"
          onClick={handleReject}
          data-testid="cookie-consent-reject"
          className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
        >
          Elutasítom
        </button>
        <button
          type="button"
          onClick={handleAccept}
          data-testid="cookie-consent-accept"
          className="flex-1 px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-white font-semibold text-sm transition-colors"
        >
          Elfogadom
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
